import Splide from '@splidejs/splide';
import GLightbox from 'glightbox';
import axios from 'axios';

const MAX_VIDEO_WIDTH = 960;
const VIEWPORT_MARGIN = 90;

export default function ShortVideoSelection() {
  const selections = document.querySelectorAll('.ShortVideoSelection-slider');

  if (!selections) return null;

  selections.forEach((selection) => {
    initSlider(selection);
    initLightbox(selection);
    initDurations(selection);
  });
}

function initSlider(selection) {
  const splide = new Splide(selection, {
    gap: '0.75rem',
    autoWidth: true,
    arrows: true,
    pagination: false,
    drag: false,
    breakpoints: {
      768: {
        arrows: false
      }
    }
  });

  splide.on('overflow', function (isOverflow) {
    splide.options = {
      arrows: isOverflow,
      drag: isOverflow
    };
  });

  splide.mount();
}

function initDurations(selection) {
  selection
    .querySelectorAll('.ShortVideo')
    .forEach((link) => readMetadata(link));
}

function readMetadata(link) {
  const videoUrl = link.getAttribute('href');
  const title = link.querySelector('.ShortVideo-title');

  if (!videoUrl || !title) return;

  const previous = title.nextElementSibling;
  if (previous && previous.classList.contains('ShortVideo-duration')) return;

  const probe = document.createElement('video');
  probe.preload = 'metadata';
  probe.muted = true;

  probe.addEventListener(
    'loadedmetadata',
    () => {
      renderDuration(title, probe.duration);
      storeNativeSize(link, probe);
    },
    { once: true }
  );

  probe.addEventListener('error', () => probe.removeAttribute('src'), {
    once: true
  });

  probe.src = videoUrl;
}

function renderDuration(title, duration) {
  if (!Number.isFinite(duration) || duration <= 0) return;

  const durationElement = document.createElement('p');
  durationElement.className = 'ShortVideo-duration';
  durationElement.textContent = `${Math.ceil(duration / 60)} min`;

  title.insertAdjacentElement('afterend', durationElement);
}

function storeNativeSize(link, media) {
  if (!media.videoWidth || !media.videoHeight) return false;

  link.dataset.nativeWidth = media.videoWidth;
  link.dataset.nativeHeight = media.videoHeight;

  return true;
}

function nativeSize(trigger) {
  const width = parseInt(trigger?.dataset.nativeWidth, 10);
  const height = parseInt(trigger?.dataset.nativeHeight, 10);

  return width && height ? { width, height } : null;
}

function isPortrait(trigger) {
  const size = nativeSize(trigger);

  return size !== null && size.height > size.width;
}

function applyPortraitFormat(slideNode, trigger) {
  const portrait = isPortrait(trigger);

  slideNode.classList.toggle('is-portrait', portrait);

  if (!portrait) return;

  const { width, height } = nativeSize(trigger);

  slideNode.style.setProperty('--short-video-ratio', `${width} / ${height}`);
  // Bornée par la hauteur native (pas d'upscale) et par les deux dimensions de la
  // fenêtre : une pleine hauteur déborderait en largeur sur un écran mobile.
  slideNode.style.setProperty(
    '--short-video-height',
    `min(${height}px, ${VIEWPORT_MARGIN}vh, calc(${VIEWPORT_MARGIN}vw * ${height} / ${width}))`
  );
}

/**
 * Format paysage : largeur du fichier d'origine, ramenée à la place disponible.
 * GLightbox fait un parseInt sur cette valeur, elle doit rester en pixels.
 */
function landscapeWidth(trigger) {
  const size = nativeSize(trigger);

  if (size === null) return `${MAX_VIDEO_WIDTH}px`;

  const width = Math.min(
    size.width,
    MAX_VIDEO_WIDTH,
    (window.innerWidth * VIEWPORT_MARGIN) / 100,
    ((window.innerHeight * VIEWPORT_MARGIN) / 100) * (size.width / size.height)
  );

  return `${Math.floor(width)}px`;
}

function initLightbox(selection) {
  let currentSlide = null;

  const lightbox = GLightbox({
    selector: `[data-code="${selection.dataset.code}"] .ShortVideo`,
    videosWidth: `${MAX_VIDEO_WIDTH}px`,
    autoplayVideos: true,
    // Sans ratio imposé, Plyr reprend le format natif du fichier. Le 16:9 par
    // défaut de GLightbox encadrait les verticales de bandes noires.
    plyr: { config: { ratio: null } }
  });

  const applyCurrentFormat = () => {
    if (!currentSlide) return;

    const { slideNode, trigger } = currentSlide;

    applyPortraitFormat(slideNode, trigger);

    // `videosWidth` est global à l'instance et relu par GLightbox à chaque
    // resize() : on le règle sur la diapo affichée. Les verticales, elles, sont
    // dimensionnées en CSS et ignorent cette largeur.
    if (!isPortrait(trigger)) {
      lightbox.settings.videosWidth = landscapeWidth(trigger);
    }

    lightbox.resize();
  };

  // Largeur du <video> injecté, y compris pour les diapos préchargées.
  lightbox.on('slide_before_load', ({ slideConfig, trigger }) => {
    slideConfig.width = landscapeWidth(trigger);
  });

  lightbox.on('slide_after_load', ({ slideNode, trigger }) => {
    applyPortraitFormat(slideNode, trigger);
  });

  lightbox.on('open', () =>
    window.addEventListener('resize', applyCurrentFormat)
  );
  lightbox.on('close', () => {
    window.removeEventListener('resize', applyCurrentFormat);
    currentSlide = null;
  });

  lightbox.on('slide_changed', ({ current }) => {
    const { player, slideIndex, trigger } = current;

    currentSlide = current;
    applyCurrentFormat();

    if (player) {
      // Métadonnées de la vignette pas encore lues : on prend celles du player.
      if (!trigger.dataset.nativeWidth) {
        waitForNativeSize(player.media, trigger, applyCurrentFormat);
      }

      player.on('ended', () => {
        if (parseInt(trigger.dataset.count) === slideIndex + 1) {
          return lightbox.close();
        }
        lightbox.nextSlide();
      });
    }
  });

  lightbox.on('slide_after_load', async (current) => {
    const { trigger } = current;
    await axios.get(
      `/front/shortvideo/ajax/update-count/${parseInt(trigger.dataset.id)}`
    );
  });

  return lightbox;
}

function waitForNativeSize(media, trigger, onReady) {
  if (!media) return;

  const store = () => storeNativeSize(trigger, media) && onReady();

  if (media.readyState >= media.HAVE_METADATA) return store();

  media.addEventListener('loadedmetadata', store, { once: true });
}
